import {
  Box,
  Container,
  Typography,
  Grid,
  Link,
} from '@mui/material';
import { Clock, TrendingDown, BanknoteX, ExternalLink } from 'lucide-react';
import Reveal from './Reveal';
import CountUp from './CountUp';
import { tokens } from '../theme';

const painPoints = [
  {
    countUp: { end: 78, suffix: '%' },
    headline: 'of customers buy from the first responder',
    subtext: 'CleoLead helps you reply in under 30 seconds, every single time.',
    icon: Clock,
    source: 'Lead Connect Study',
    sourceUrl: 'https://www.vendasta.com/blog/lead-response-time/',
  },
  {
    countUp: { end: 73, suffix: '%' },
    headline: 'of leads are never contacted by sales teams',
    subtext: 'AI-assisted tools help qualify leads while your team focuses on closing.',
    icon: TrendingDown,
    source: 'Kixie Research',
    sourceUrl: 'https://www.kixie.com/sales-blog/speed-to-lead-response-time-statistics-that-drive-conversions/',
  },
  {
    countUp: { end: 10.5, decimals: 1, prefix: '$', suffix: 'k' },
    headline: 'average monthly cost of missed calls for SMBs',
    subtext: 'AI-assisted first response means you never miss a sales opportunity.',
    icon: BanknoteX,
    source: 'Dialora.ai Report',
    sourceUrl: 'https://www.dialora.ai/blog/missed-call-costs-smbs-revenue-loss-ai-solutions',
  },
];

export default function PainPointsSection() {
  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: tokens.bandAlt,
        borderTop: `1px solid ${tokens.border}`,
        borderBottom: `1px solid ${tokens.border}`,
      }}
    >
      <Container maxWidth="lg">
        <Reveal sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="overline" sx={{ display: 'block', mb: 1 }}>
            The Problem
          </Typography>
          <Typography variant="h2" sx={{ mb: 2 }}>
            Every Minute of Silence Costs You Money
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              maxWidth: 560,
              mx: 'auto',
            }}
          >
            Customers call, DM, and message you at every hour. If nobody picks up, they move on. Here's what the data shows:
          </Typography>
        </Reveal>

        <Grid container spacing={3}>
          {painPoints.map((point, index) => {
            const Icon = point.icon;
            return (
              <Grid size={{ xs: 12, md: 4 }} key={index}>
                <Reveal delay={index * tokens.motion.stagger} sx={{ height: '100%' }}>
                  <Box
                    sx={{
                      p: 4,
                      borderRadius: `${tokens.radiusCard}px`,
                      bgcolor: tokens.bgPaper,
                      border: `1px solid ${tokens.border}`,
                      boxShadow: tokens.shadowSoft,
                      height: '100%',
                      transition: `box-shadow ${tokens.motion.micro}ms ease, border-color ${tokens.motion.micro}ms ease`,
                      '&:hover': {
                        borderColor: '#D9CDBC',
                        boxShadow: tokens.shadowLift,
                      },
                    }}
                  >
                    {/* icon */}
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: `${tokens.radiusControl}px`,
                        bgcolor: tokens.copperTint,
                        color: tokens.copper,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                      }}
                    >
                      <Icon size={22} strokeWidth={1.5} />
                    </Box>

                    {/* big stat */}
                    <Typography
                      sx={{
                        fontFamily: tokens.fontDisplay,
                        fontSize: { xs: '2.6rem', md: '3rem' },
                        fontWeight: 700,
                        lineHeight: 1.05,
                        letterSpacing: '-0.01em',
                        color: tokens.copper,
                        mb: 1,
                      }}
                    >
                      <CountUp {...point.countUp} />
                    </Typography>

                    <Typography
                      variant="h6"
                      sx={{
                        color: 'text.primary',
                        fontWeight: 600,
                        lineHeight: 1.4,
                        mb: 1.5,
                      }}
                    >
                      {point.headline}
                    </Typography>

                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                      {point.subtext}
                    </Typography>

                    <Link
                      href={point.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="none"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.6,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        color: 'text.secondary',
                        borderRadius: '999px',
                        border: `1px solid ${tokens.border}`,
                        bgcolor: tokens.bgDefault,
                        px: 1.4,
                        py: 0.5,
                        transition: `color ${tokens.motion.micro}ms ease, border-color ${tokens.motion.micro}ms ease`,
                        '&:hover': {
                          color: tokens.copper,
                          borderColor: tokens.copper,
                        },
                      }}
                    >
                      <ExternalLink size={12} strokeWidth={1.5} />
                      {point.source}
                    </Link>
                  </Box>
                </Reveal>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
}
